import LightHeader from "components/Headers/LightHeader";
import HoursByServiceOrderTableHeader from "components/TableHeaders/admin/HoursByServiceOrderTableHeader";
import SglTable from "components/ToolkitTable/SglTable";
import PrintableReportChamados from "components/Pdf/PrintableReportChamados";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BrowserView, MobileView } from "react-device-detect";
import Skeleton from "react-loading-skeleton";
import { Button, Card, CardBody, CardHeader, Container, Row } from "reactstrap";
import api from "services/api";

const columns = [
  { dataField: "date", text: "Data", sort: true },
  { dataField: "clienteNome", text: "Cliente", sort: true },
  { dataField: "numero", text: "Nº Chamado", sort: true },
  { dataField: "descricao", text: "Descrição", sort: true },
  { dataField: "horas", text: "Horas", sort: true },
  { dataField: "projectName", text: "Projeto", sort: true },
];

export default function HoursByServiceOrder() {
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [project, setProject] = useState("ALL");
  const [dateTo, setDateTo] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [numeroChamado, setNumeroChamado] = useState("");
  const [loading, setLoading] = useState(false);
  const [consultants, setConsultants] = useState([]);
  const [showPrint, setShowPrint] = useState(false);

  const projects = useMemo(() => {
    if (!customer?.projects) return [];
    return [{ _id: "ALL", name: "Todos os projetos" }, ...customer.projects];
  }, [customer]);

  // usado para achar o nome do projeto (e do cliente dono dele) quando
  // nenhum Cliente foi selecionado (filtro por Nº Chamado funciona
  // independente de Cliente)
  const allProjects = useMemo(
    () =>
      customers.flatMap((c) =>
        (c.projects || []).map((p) => ({ ...p, clienteNome: c.shortName }))
      ),
    [customers]
  );

  const selectCustomer = (id) => {
    setProject("ALL");
    const cust = customers.find((customer) => customer._id === id);
    setCustomer(cust);
  };

  const fetchProjects = useCallback(async () => {
    try {
      const customersResponse = await api.get("customer/allWithProjects");
      setCustomers(customersResponse.data);
    } catch (error) {
      console.error("Erro ao buscar clientes:", error);
    }
  }, []);

  const getTableData = () => {
    const chamados = [];

    consultants.forEach((item) => {
      const projectObj = allProjects.find((p) => p._id === item.project);
      const projectName = projectObj?.name || "Projeto não encontrado";
      const clienteNome = projectObj?.clienteNome || "";

      if (item.chamados && item.chamados.length > 0) {
        item.chamados.forEach((chamado) => {
          const filtroNumeroChamado = numeroChamado.trim();
          if (
            filtroNumeroChamado &&
            !chamado.numero?.includes(filtroNumeroChamado)
          ) {
            return;
          }

          const rawDate = chamado.date || item.date || null;
          let formattedDate = "";
          let sortableDate = null;

          if (rawDate) {
            let dateObj = new Date(rawDate);
            const offset = dateObj.getTimezoneOffset();
            dateObj = new Date(dateObj.getTime() + offset * 60000);

            if (!isNaN(dateObj.getTime())) {
              sortableDate = dateObj;
              const day = String(dateObj.getDate()).padStart(2, "0");
              const month = String(dateObj.getMonth() + 1).padStart(2, "0");
              const year = dateObj.getFullYear();
              formattedDate = `${day}-${month}-${year}`;
            }
          }

          chamados.push({
            date: formattedDate,
            sortableDate,
            projectName,
            clienteNome,
            numero: chamado.numero,
            descricao: chamado.descricao,
            horas: chamado.horas || "00:00",
          });
        });
      }
    });

    chamados.sort((a, b) => {
      if (!a.sortableDate || !b.sortableDate) return 0;
      return a.sortableDate - b.sortableDate;
    });

    return chamados;
  };

  const agruparChamadosPorProjeto = (chamados) => {
    return chamados.reduce((acc, chamado) => {
      const key = chamado.projectName;
      if (!acc[key]) acc[key] = [];
      acc[key].push(chamado);
      return acc;
    }, {});
  };

  const fetchConsultants = useCallback(async () => {
    setLoading(true);
    try {
      let projectIdsToSend = [];
      if (project === "ALL" || !project) {
        projectIdsToSend = customer?.projects?.map((p) => p._id) || [];
      } else {
        projectIdsToSend = [project];
      }

      const consultantsResponse = await api.post("user/hoursByServiceOrder", {
        projects: projectIdsToSend,
        dateTo,
        dateFrom,
      });
      console.log("Consultants response:", consultantsResponse.data);
      setConsultants(consultantsResponse.data);
    } catch (error) {
      console.error("Erro ao buscar chamados:", error);
      alert("Ocorreu um erro ao carregar os chamados.");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, project, customer]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const chamados = getTableData();
  const groupedData = agruparChamadosPorProjeto(chamados);

  useEffect(() => {
    if (showPrint) {
      const gerarPdfAsync = async () => {
        if (!customer?.fullName) {
          alert("Cliente não selecionado");
          setShowPrint(false);
          return;
        }

        const pdf = new jsPDF();
        const totalPages = Object.keys(groupedData).length;

        for (let i = 0; i < totalPages; i++) {
          const input = document.getElementById(`divToPrint-${i}`);
          if (!input) {
            alert("Elemento para imprimir não encontrado");
            continue;
          }
          const canvas = await html2canvas(input, { scale: 2 });
          const imgData = canvas.toDataURL("image/png");
          pdf.addImage(imgData, "PNG", 10, 10, 190, 277);
          if (i < totalPages - 1) pdf.addPage();
        }

        pdf.save("Relatorio-Chamados.pdf");
        setShowPrint(false);
      };

      gerarPdfAsync();
    }
  }, [showPrint, customer?.fullName, groupedData]);

  return (
    <>
      <LightHeader
        title="Relatório de chamados por ordem de serviço"
        description="Rel. chamados por O.S."
        menuTitle="Rel. chamados por O.S."
        parent="Relatórios"
      />
      <Container className="mt--6" fluid style={{ overflowY: "scroll" }}>
        <Row>
          <div className="col">
            <Card>
              <CardHeader>
                <BrowserView>
                  <HoursByServiceOrderTableHeader
                    projects={projects}
                    customers={customers}
                    project={project}
                    customer={customer}
                    dateTo={dateTo}
                    dateFrom={dateFrom}
                    numeroChamado={numeroChamado}
                    setNumeroChamado={setNumeroChamado}
                    selectCustomer={selectCustomer}
                    setProject={setProject}
                    setDateFrom={setDateFrom}
                    setDateTo={setDateTo}
                    fetchConsultants={fetchConsultants}
                  />
                </BrowserView>
                <MobileView />
              </CardHeader>
              <CardBody
                style={{ overflowX: "auto", minHeight: "10vh", padding: "0px" }}
              >
                <BrowserView>
                  {loading ? (
                    <center>
                      <Skeleton
                        count={5}
                        width="95%"
                        height={30}
                        borderRadius="5px"
                        inline
                      />
                    </center>
                  ) : (
                    <SglTable
                      columns={columns}
                      data={chamados}
                      rowClick={() => {}}
                      extraControls={
                        <Button
                          color="primary"
                          onClick={() => setShowPrint(true)}
                          disabled={consultants.length === 0}
                        >
                          Baixar PDF
                        </Button>
                      }
                    />
                  )}
                </BrowserView>
                <MobileView />
              </CardBody>
            </Card>
          </div>
        </Row>
      </Container>

      {showPrint && (
        <div style={{ position: "fixed", top: "-10000px", left: "-10000px" }}>
          <PrintableReportChamados
            groupedData={groupedData}
            customerName={customer?.fullName || ""}
            dateFrom={dateFrom}
            dateTo={dateTo}
          />
        </div>
      )}
    </>
  );
}
